'use client'

import { createAuthClient } from 'better-auth/react'
import { adminClient, emailOTPClient, organizationClient } from 'better-auth/client/plugins'

/**
 * Better Auth client for the web. Cookies are handled by the browser;
 * cross-origin works because the API sets `SameSite=None; Secure` on
 * its cookies and CORS allows credentials from `*.kohjiaglenn.workers.dev`.
 *
 * Reads `NEXT_PUBLIC_API_URL` which is inlined at build time by
 * `next.config.ts` based on `NEXT_PUBLIC_ENV`.
 *
 * We import `createAuthClient` from `better-auth/react` directly rather
 * than going through `@ideight/api-client/auth` — the wrapper there
 * exists for native (where Expo plugins matter); on web there's no extra
 * config to share, and routing the import through the package leaks
 * internal Better Auth type paths into our `tsc --noEmit` graph.
 */
const baseURL = process.env.NEXT_PUBLIC_API_URL

if (!baseURL) {
  throw new Error('NEXT_PUBLIC_API_URL is not set — check next.config.ts.')
}

// `basePath: '/auth'` matches the server-side `basePath` in
// apps/api/src/lib/auth.ts. Without it, the client would hit
// `/api/auth/sign-up/email` (Better Auth's default) and the API would
// 404.
/**
 * Plugins mirror the server-side wiring in `apps/api/src/lib/auth.ts`:
 *   - `organizationClient` exposes `authClient.organization.*` (create,
 *     setActive, list) and the `useListOrganizations` /
 *     `useActiveOrganization` React hooks.
 *   - `adminClient` exposes `authClient.admin.*` (listUsers, banUser,
 *     impersonateUser, setRole).
 *   - `emailOTPClient` exposes `authClient.emailOtp.*`
 *     (`sendVerificationOtp`, `verifyEmail`) for the OTP email-verification
 *     flow that gates signup/sign-in.
 * If one side adds a plugin and the other doesn't, requests 404. Keep
 * the two `plugins` arrays in lockstep.
 */
export const authClient = createAuthClient({
  baseURL,
  basePath: '/auth',
  plugins: [organizationClient(), adminClient(), emailOTPClient()],
})

export const useSession = authClient.useSession
